import { inject as injectBranding } from '@angular/core';
import { SiteBrandingService } from '../../services/site-branding.service';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  OnInit
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule, NgForm } from '@angular/forms';

import { Location } from '../../models/app.models';
import { LocationService } from '../../services/location.service';
import {
  ApplicationsService,
  ApplicationRequest
} from '../../services/applications.service';
import { ToastService } from '../../services/toast.service';

type ApplicationOption = 'mchire' | 'direct' | null;

@Component({
  selector: 'app-applications',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule
  ],
  templateUrl: './applications.component.html',
  styleUrls: ['./applications.component.css'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class ApplicationsComponent implements OnInit {
  readonly branding = injectBranding(SiteBrandingService);
  readonly positions = [
    'Crew Member',
    'Shift Manager',
    'Management',
    'Maintenance',
    'Other'
  ];

  readonly ageOptions = [
    'Under 16',
    '16–17',
    '18+'
  ];

  readonly shiftOptions = [
    'Morning',
    'Afternoon',
    'Dinner',
    'Evening',
    'Overnight'
  ];

  readonly availabilityOptions = [
    'Monday',
    'Tuesday',
    'Wednesday',
    'Thursday',
    'Friday',
    'Saturday',
    'Sunday',
    'Anytime'
  ];

  activeApplicationOption: ApplicationOption = null;

  locations: Location[] = [];
  locationsLoading = true;

  isSubmitting = false;

  selectedResume: File | null = null;
  selectedResumeName = '';

  applicationRequest: ApplicationRequest =
    this.createEmptyRequest();

  constructor(
    private readonly locationService: LocationService,
    private readonly applicationsService: ApplicationsService,
    private readonly toastService: ToastService,
    private readonly cdr: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    this.loadLocations();
  }

  toggleApplicationOption(
    option: Exclude<ApplicationOption, null>
  ): void {
    this.activeApplicationOption =
      this.activeApplicationOption === option
        ? null
        : option;
  }

  // =========================================================
  // LOCATIONS
  // =========================================================

  loadLocations(): void {
    this.locationsLoading = true;

    this.locationService
      .getLocations()
      .subscribe({
        next: locations => {
          this.locations = locations
            .filter(location => location.isActive)
            .sort(
              (a, b) =>
                a.displayOrder - b.displayOrder ||
                a.name.localeCompare(b.name)
            );

          this.locationsLoading = false;
          this.cdr.detectChanges();
        },

        error: error => {
          console.error(
            'Unable to load application locations:',
            error
          );

          this.locationsLoading = false;

          this.toastService.error(
            'Unable to load restaurant locations.'
          );

          this.cdr.detectChanges();
        }
      });
  }

  // =========================================================
  // SHIFT / AVAILABILITY
  // =========================================================

  toggleShift(shift: string): void {
    this.applicationRequest.preferredShifts =
      this.toggleSelection(
        this.applicationRequest.preferredShifts,
        shift
      );
  }

  toggleAvailability(availability: string): void {
    if (availability === 'Anytime') {
      const isAnytimeSelected =
        this.applicationRequest.availability.includes(
          'Anytime'
        );

      this.applicationRequest.availability =
        isAnytimeSelected
          ? []
          : ['Anytime'];

      return;
    }

    const withoutAnytime =
      this.applicationRequest.availability
        .filter(item => item !== 'Anytime');

    this.applicationRequest.availability =
      this.toggleSelection(
        withoutAnytime,
        availability
      );
  }

  isShiftSelected(shift: string): boolean {
    return this.applicationRequest
      .preferredShifts
      .includes(shift);
  }

  isAvailabilitySelected(
    availability: string
  ): boolean {
    return this.applicationRequest
      .availability
      .includes(availability);
  }

  // =========================================================
  // RESUME
  // =========================================================

  onResumeSelected(event: Event): void {
    const input =
      event.target as HTMLInputElement;

    const file = input.files?.[0];

    if (!file)
      return;

    const allowedExtensions = [
      '.pdf',
      '.doc',
      '.docx',
      '.txt'
    ];

    const extension = file.name
      .toLowerCase()
      .substring(file.name.lastIndexOf('.'));

    if (!allowedExtensions.includes(extension)) {
      this.toastService.error(
        'Resume must be a PDF, Word document, or text file.'
      );

      input.value = '';
      return;
    }

    const maxFileSize = 8 * 1024 * 1024;

    if (file.size > maxFileSize) {
      this.toastService.error(
        'Resume files cannot exceed 8 MB.'
      );

      input.value = '';
      return;
    }

    this.selectedResume = file;
    this.selectedResumeName = file.name;

    this.cdr.detectChanges();
  }

  removeResume(): void {
    this.selectedResume = null;
    this.selectedResumeName = '';

    this.cdr.detectChanges();
  }

  // =========================================================
  // SUBMIT
  // =========================================================

  submitApplication(form: NgForm): void {
    if (!this.isFormValid()) {
      this.toastService.error(
        'Complete all required fields.'
      );

      return;
    }

    this.isSubmitting = true;

    this.applicationsService
      .submitApplication(
        this.applicationRequest,
        this.selectedResume
      )
      .subscribe({
        next: () => {
          this.isSubmitting = false;

          this.applicationRequest =
            this.createEmptyRequest();

          this.selectedResume = null;
          this.selectedResumeName = '';

          form.resetForm(
            this.applicationRequest
          );

          this.toastService.success(
            'Application submitted successfully.'
          );

          this.cdr.detectChanges();
        },

        error: error => {
          this.isSubmitting = false;

          const message =
            typeof error.error === 'string'
              ? error.error
              : error.error?.message ??
                'Unable to submit your application.';

          this.toastService.error(message);

          this.cdr.detectChanges();
        }
      });
  }

  // =========================================================
  // HELPERS
  // =========================================================

  private isFormValid(): boolean {
  return Boolean(
    this.applicationRequest.locationId &&
    this.applicationRequest.position &&
    this.applicationRequest.firstName.trim() &&
    this.applicationRequest.lastName.trim() &&
    this.applicationRequest.phone.trim() &&
    this.applicationRequest.email.trim() &&
    this.applicationRequest.ageEligibility &&
    this.applicationRequest.preferredShifts.length &&
    this.applicationRequest.availability.length &&
    this.applicationRequest.consentToContact
  );
}

  private toggleSelection(
    current: string[],
    value: string
  ): string[] {
    return current.includes(value)
      ? current.filter(item => item !== value)
      : [...current, value];
  }

  private createEmptyRequest():
    ApplicationRequest {
    return {
      locationId: null,
      position: '',
      firstName: '',
      lastName: '',
      phone: '',
      email: '',
      ageEligibility: '',
      preferredShifts: [],
      availability: [],
      howHeard: '',
      additionalInformation: '',
      consentToContact: false
    };
  }
}
